import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree as ChainIcon,
  DataObject as ObjectIcon,
  Functions as FunctionIcon,
  ListAlt as RunIcon,
  Search as SearchIcon,
  TravelExplore as RequestIcon,
  Web as PageIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

export interface QuickSearchPage {
  text: string;
  path: string;
  description?: string;
}

interface Result {
  key: string;
  group: 'Go to' | 'Functions' | 'Chains' | 'Objects' | 'Pages';
  label: string;
  detail?: string;
  path: string;
  icon: React.ReactNode;
}

interface Named {
  id: string;
  name: string;
  display_name?: string;
  description?: string;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const LIST_TTL_MS = 60_000;
const MAX_PER_GROUP = 6;

let listCache: { at: number; chains: Named[]; objects: Named[] } | null = null;

const matches = (item: Named, q: string) =>
  [item.name, item.display_name, item.description].some(v => v && v.toLowerCase().includes(q));

/** Cmd/Ctrl+K palette: jump to a page, function, chain or object, or paste a run / request ID. */
export const QuickSearch: React.FC<{ pages: QuickSearchPage[] }> = ({ pages }) => {
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [functions, setFunctions] = useState<Named[]>([]);
  const [lists, setLists] = useState<{ chains: Named[]; objects: Named[] }>({ chains: [], objects: [] });
  const [idHits, setIdHits] = useState<Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const authed = useCallback(
    async (url: string) => {
      const token = await getIdToken();
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    },
    [getIdToken],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (listCache && Date.now() - listCache.at < LIST_TTL_MS) {
      setLists(listCache);
      return;
    }
    let cancelled = false;
    Promise.allSettled([authed('/api/v2/chains/'), authed('/api/v2/custom-objects/?include_public=false&page_size=100')]).then(
      ([chains, objects]) => {
        if (cancelled) return;
        const next = {
          chains: chains.status === 'fulfilled' && Array.isArray(chains.value) ? chains.value : [],
          objects: objects.status === 'fulfilled' ? objects.value?.results || [] : [],
        };
        listCache = { at: Date.now(), ...next };
        setLists(next);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, authed]);

  const q = query.trim().toLowerCase();
  const pastedId = query.match(UUID_RE)?.[0] || null;

  useEffect(() => {
    if (!open || q.length < 2 || pastedId) {
      setFunctions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      authed(`/api/v2/ai-functions/?search=${encodeURIComponent(q)}&latest_only=true&include_public=false&page_size=${MAX_PER_GROUP}`)
        .then(d => !cancelled && setFunctions(d.items || []))
        .catch(() => !cancelled && setFunctions([]))
        .finally(() => !cancelled && setLoading(false));
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, q, pastedId, authed]);

  useEffect(() => {
    if (!pastedId) {
      setIdHits([]);
      return;
    }
    let cancelled = false;
    const id = pastedId;
    const request: Result = {
      key: 'request',
      group: 'Go to',
      label: 'Look up platform request',
      detail: id,
      path: `/dashboard/request-lookup?id=${encodeURIComponent(id)}`,
      icon: <RequestIcon fontSize="small" />,
    };
    setIdHits([request]);
    setLoading(true);
    Promise.allSettled([authed(`/api/v2/analytics/runs/${id}`), authed(`/api/v2/chains/executions/${id}`)]).then(([run, chainRun]) => {
      if (cancelled) return;
      const hits: Result[] = [];
      if (run.status === 'fulfilled') {
        hits.push({
          key: 'run',
          group: 'Go to',
          label: `Function run: ${run.value?.data?.function_name || id}`,
          detail: id,
          path: `/dashboard/run/${id}`,
          icon: <RunIcon fontSize="small" />,
        });
      }
      if (chainRun.status === 'fulfilled') {
        hits.push({
          key: 'chain-run',
          group: 'Go to',
          label: 'Chain run',
          detail: id,
          path: `/dashboard/chain-run/${id}`,
          icon: <ChainIcon fontSize="small" />,
        });
      }
      setIdHits([...hits, request]);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [pastedId, authed]);

  const results = useMemo<Result[]>(() => {
    if (pastedId) return idHits;
    const out: Result[] = [];
    const pageHits = q ? pages.filter(p => p.text.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)) : pages;
    out.push(
      ...functions.slice(0, MAX_PER_GROUP).map(f => ({
        key: `f-${f.id}`,
        group: 'Functions' as const,
        label: f.display_name || f.name,
        detail: f.description,
        path: `/dashboard/function-runs/${f.id}?tab=performance`,
        icon: <FunctionIcon fontSize="small" />,
      })),
    );
    if (q) {
      out.push(
        ...lists.chains
          .filter(c => matches(c, q))
          .slice(0, MAX_PER_GROUP)
          .map(c => ({
            key: `c-${c.id}`,
            group: 'Chains' as const,
            label: c.name,
            detail: c.description,
            path: `/dashboard/chain-performance/${c.id}`,
            icon: <ChainIcon fontSize="small" />,
          })),
        ...lists.objects
          .filter(o => matches(o, q))
          .slice(0, MAX_PER_GROUP)
          .map(o => ({
            key: `o-${o.id}`,
            group: 'Objects' as const,
            label: o.display_name || o.name,
            detail: o.description,
            path: `/dashboard/objects/generate?object=${o.id}`,
            icon: <ObjectIcon fontSize="small" />,
          })),
      );
    }
    out.push(
      ...pageHits.slice(0, q ? MAX_PER_GROUP : 12).map(p => ({
        key: `p-${p.path}-${p.text}`,
        group: 'Pages' as const,
        label: p.text,
        detail: p.description,
        path: p.path,
        icon: <PageIcon fontSize="small" />,
      })),
    );
    return out;
  }, [pastedId, idHits, q, pages, functions, lists]);

  useEffect(() => setActive(0), [query]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const choose = (r: Result | undefined) => {
    if (!r) return;
    close();
    navigate(r.path);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[active]);
    }
  };

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
  let lastGroup = '';

  return (
    <>
      <Tooltip title={`Search functions, chains, objects, pages, or paste a run or request ID (${isMac ? '⌘' : 'Ctrl+'}K)`}>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.15)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            width: 32,
            height: 32,
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm" PaperProps={{ sx: { position: 'fixed', top: 72, m: 0 } }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search functions, chains, objects and pages, or paste a run or request ID"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List ref={listRef} dense sx={{ maxHeight: 420, overflow: 'auto', py: 0 }}>
          {results.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {loading ? 'Searching…' : 'No matches.'}
            </Typography>
          )}
          {results.map((r, i) => {
            const header = r.group !== lastGroup ? r.group : null;
            lastGroup = r.group;
            return (
              <React.Fragment key={r.key}>
                {header && <ListSubheader sx={{ lineHeight: '28px', fontSize: '0.7rem', textTransform: 'uppercase' }}>{header}</ListSubheader>}
                <ListItemButton data-index={i} selected={i === active} onClick={() => choose(r)} onMouseMove={() => setActive(i)}>
                  <ListItemIcon sx={{ minWidth: 32 }}>{r.icon}</ListItemIcon>
                  <ListItemText
                    primary={r.label}
                    secondary={r.detail}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              </React.Fragment>
            );
          })}
        </List>
        <Box sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip size="small" variant="outlined" label="↑↓ to move" />
          <Chip size="small" variant="outlined" label="Enter to open" />
          <Chip size="small" variant="outlined" label="Esc to close" />
        </Box>
      </Dialog>
    </>
  );
};
